import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchJson, postJson } from '../api';
import BoostSheet from '../components/BoostSheet';
import PollTiles from '../components/PollTiles';
import { AskingHeader, ShareRow, SourceSplit, VoterLog, applyTallies, optimisticVote, votesLine } from '../components/PollView';
import Toast, { useToast } from '../components/Toast';
import { HeartIcon, ShareIcon } from '../components/Icons';
import { useAuth } from '../lib/auth';
import { trackVote } from '../lib/analytics';
import { formatCount } from '../lib/format';
import { displayUrl, sharePoll } from '../lib/share';
import useLivePoll from '../lib/useLivePoll';

const COMMENT_MAX_LENGTH = 1000;

function updatePollComment(poll, commentId, updates) {
  if (!poll) return poll;
  return {
    ...poll,
    comments: (poll.comments || []).map((comment) => (comment.id === commentId ? { ...comment, ...updates } : comment)),
  };
}

function LikeIcon({ active }) {
  return <HeartIcon className={`h-3.5 w-3.5 ${active ? 'text-voto-coral' : 'text-voto-muted'}`} />;
}

function BoostStatus({ boost }) {
  if (!boost) return null;
  const wanted = boost.votes_wanted || 0;
  const delivered = Math.min(boost.votes_delivered || 0, wanted);
  const pct = wanted ? Math.round((delivered / wanted) * 100) : 0;
  return (
    <div className="rounded-xl bg-voto-surface px-3.5 py-2.5 text-sm ring-1 ring-voto-line" data-testid="boost-status">
      <p className="flex justify-between text-voto-fg">
        <span>{delivered >= wanted ? 'Boost delivered' : 'Boosted'}</span>
        <span className="text-voto-muted">{formatCount(delivered)} of {formatCount(wanted)} votes</span>
      </p>
      <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-voto-raised" aria-hidden="true">
        <span className="block h-full bg-voto-coral" style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
}

export default function PollDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user || null;
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voting, setVoting] = useState(false);
  const [voteError, setVoteError] = useState('');
  const [boostOpen, setBoostOpen] = useState(false);
  const [toast, showToast] = useToast();
  const [commentBody, setCommentBody] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [reportedComments, setReportedComments] = useState({});

  const load = useCallback(async (signal) => {
    const data = await fetchJson(`/polls/${id}`, signal ? { signal } : {});
    setPoll((current) => {
      if (!current) return data;
      // A live refresh replaces tallies/voters but keeps locally added comments
      // state (likes) only if the server didn't send comments.
      return { ...data, voted_item_id: data.voted_item_id ?? current.voted_item_id, comments: data.comments ?? current.comments };
    });
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setPoll(null);
    load(controller.signal)
      .catch((err) => { if (err.name !== 'AbortError') setError('Unable to load this poll.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [load]);

  const isMine = !!user && poll?.author_id != null && poll.author_id === user.id;
  const revealed = !!poll && (poll.voted_item_id != null || isMine);
  // Results update live for the asker (and for anyone who has voted).
  useLivePoll(!!poll && revealed, () => { load().catch(() => {}); }, 10000);

  async function vote(item) {
    if (!poll || voting || revealed) return;
    setVoting(true);
    setVoteError('');
    const before = poll;
    setPoll(optimisticVote(poll, item));
    try {
      const data = await postJson(`/polls/${poll.id}/vote`, { item_id: item.id, source: 'detail' });
      trackVote('detail', { poll_id: poll.id });
      setPoll((current) => applyTallies(current, data, item.id));
    } catch (err) {
      if (err.status === 409) {
        setPoll({ ...before, voted_item_id: before.voted_item_id ?? item.id });
        setVoteError('You already voted on this poll.');
      } else {
        setPoll(before);
        setVoteError('Unable to record your vote right now.');
      }
    } finally {
      setVoting(false);
    }
  }

  async function shareAsVoter() {
    try {
      const { url, channel } = await sharePoll(poll, { where: 'poll_page' });
      if (channel === 'copy') showToast(`Link copied · ${displayUrl(url)}`);
    } catch (_) {
      showToast('Sharing isn’t available here.');
    }
  }

  async function handleCommentSubmit(event) {
    event.preventDefault();
    const trimmedBody = commentBody.trim();
    if (!trimmedBody) {
      setCommentError('Add a comment before posting.');
      return;
    }
    setCommentSubmitting(true);
    setCommentError('');
    try {
      const comment = await postJson(`/polls/${id}/comments`, { body: trimmedBody });
      setPoll((current) => (current ? {
        ...current,
        comments_count: comment.comments_count ?? current.comments_count,
        comments: [comment, ...(current.comments || [])],
      } : current));
      setCommentBody('');
    } catch (err) {
      setCommentError(err.status === 422 && err.detail ? err.detail : 'Unable to post this comment right now.');
    } finally {
      setCommentSubmitting(false);
    }
  }

  async function reportComment(commentId) {
    if (reportedComments[commentId]) return;
    try {
      await postJson(`/comments/${commentId}/flag`, {});
      setReportedComments((m) => ({ ...m, [commentId]: true }));
    } catch (_) {
      // best-effort
    }
  }

  async function handleCommentLike(comment) {
    if (!user) {
      navigate('/auth');
      return;
    }
    const wasLiked = !!comment.liked_by_current_user;
    const previousCount = comment.likes_count || 0;
    setCommentError('');
    setPoll((current) => updatePollComment(current, comment.id, {
      likes_count: Math.max(0, previousCount + (wasLiked ? -1 : 1)),
      liked_by_current_user: !wasLiked,
    }));
    try {
      const state = wasLiked
        ? await fetchJson(`/comments/${comment.id}/like`, { method: 'DELETE' })
        : await postJson(`/comments/${comment.id}/like`, {});
      setPoll((current) => updatePollComment(current, comment.id, {
        likes_count: state.likes_count,
        liked_by_current_user: state.liked_by_current_user,
      }));
    } catch (err) {
      setPoll((current) => updatePollComment(current, comment.id, { likes_count: previousCount, liked_by_current_user: wasLiked }));
      setCommentError('Unable to update this like right now.');
    }
  }

  return (
    <main className="mx-auto w-full max-w-xl px-4 pb-10 pt-4">
      <Link className="rounded text-sm font-medium text-voto-muted hover:text-voto-fg" to="/feed">
        ‹ {isMine ? 'Your poll' : 'Back to feed'}
      </Link>

      {loading && <p className="mt-6 text-voto-muted">Loading poll…</p>}
      {!loading && error && <p className="mt-6 rounded-lg bg-voto-coral/10 p-3 text-voto-coral">{error}</p>}

      {!loading && !error && poll && (
        <>
          <div className="mt-4 flex flex-col gap-3">
            {!isMine && (
              <AskingHeader poll={poll} sub={revealed ? `${votesLine(poll)} so far` : 'Tap a photo to vote'} />
            )}
            <h1 className="text-[22px] font-semibold leading-tight text-voto-fg">{poll.caption}</h1>
            {isMine && <SourceSplit poll={poll} />}
            {isMine && poll.visibility === 'link' && (
              <p className="text-xs text-voto-muted">Only people with the link can vote on this poll.</p>
            )}
            <PollTiles
              src={poll.image_url}
              items={poll.items}
              count={poll.items_count || poll.items?.length}
              alt={poll.caption || 'Poll image'}
              variant="rounded"
              onVote={revealed ? null : vote}
              revealed={revealed}
              votedItemId={poll.voted_item_id ?? null}
              totalVotes={poll.votes_count || 0}
              voting={voting}
              votersByItem={poll.voters_by_item || null}
            />
            {voteError && <p className="text-sm text-voto-coral">{voteError}</p>}

            {isMine ? (
              <>
                <ShareRow poll={poll} onToast={showToast} />
                <Toast message={toast} />
                <BoostStatus boost={poll.boost} />
                <button
                  type="button"
                  onClick={() => setBoostOpen(true)}
                  className="rounded-[14px] bg-voto-surface px-4 py-3 text-[15px] font-semibold text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised"
                >
                  {poll.boost && (poll.boost.votes_delivered || 0) < (poll.boost.votes_wanted || 0) ? 'Boost more' : 'Boost'}
                  <span className="font-normal text-voto-muted"> · 1 credit per vote</span>
                </button>
                <section className="mt-2" aria-labelledby="voter-log-title">
                  <h2 id="voter-log-title" className="mb-3 text-sm font-semibold text-voto-muted">Votes</h2>
                  <VoterLog poll={poll} />
                </section>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between px-1 text-[13px]">
                  <span className="flex items-center gap-2 text-voto-muted">
                    {revealed ? (
                      <>
                        <span className="h-2 w-2 rounded-full bg-voto-green" aria-hidden="true" />
                        Results update live
                      </>
                    ) : (
                      'Tap a photo to vote.'
                    )}
                  </span>
                  <button type="button" onClick={shareAsVoter} className="flex items-center gap-1.5 rounded-md px-1 py-1 font-semibold text-voto-coral hover:underline">
                    <ShareIcon />
                    Share
                  </button>
                </div>
                <Toast message={toast} />
              </>
            )}
          </div>

          {isMine && (
            <BoostSheet
              poll={poll}
              open={boostOpen}
              onClose={() => setBoostOpen(false)}
              onBoosted={(boost) => {
                setPoll((current) => ({ ...current, boost }));
                showToast('Boosted · your poll is at the top of the Voting Booth');
              }}
            />
          )}

          <section className="mt-8">
            <h2 className="text-base font-semibold text-voto-fg">Comments</h2>
            {user ? (
              <form className="mt-3 rounded-xl bg-voto-surface p-4 ring-1 ring-voto-line" onSubmit={handleCommentSubmit}>
                <label className="block text-sm font-medium text-voto-fg" htmlFor="comment-body">
                  Comment as {user.name || `@${user.handle}`}
                </label>
                <textarea
                  id="comment-body"
                  className="mt-2 min-h-28 w-full rounded-lg border border-voto-line bg-voto-bg px-3 py-2 text-sm text-voto-fg outline-none transition focus:border-voto-coral"
                  maxLength={COMMENT_MAX_LENGTH}
                  onChange={(event) => setCommentBody(event.target.value)}
                  placeholder="Add your comment"
                  value={commentBody}
                />
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-voto-muted">{commentBody.length}/{COMMENT_MAX_LENGTH}</p>
                  <button
                    className="rounded-lg bg-voto-coral px-4 py-2 text-sm font-semibold text-white transition hover:bg-voto-coralDeep disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={commentSubmitting}
                    type="submit"
                  >
                    {commentSubmitting ? 'Posting...' : 'Post comment'}
                  </button>
                </div>
                {commentError && <p className="mt-2 text-xs text-voto-coral">{commentError}</p>}
              </form>
            ) : (
              <p className="mt-3 rounded-xl bg-voto-surface p-4 text-sm text-voto-muted ring-1 ring-voto-line">
                <Link to="/auth" className="font-semibold text-voto-coral hover:underline">Sign in</Link> to join the conversation.
              </p>
            )}
            {!poll.comments?.length && (
              <p className="mt-3 rounded-xl bg-voto-surface p-4 text-voto-muted">No comments yet.</p>
            )}
            {!!poll.comments?.length && (
              <ul className="mt-3 space-y-2">
                {poll.comments.map((comment) => (
                  <li key={comment.id} className="rounded-xl bg-voto-surface p-3">
                    <p className="text-xs font-medium text-voto-muted">{comment.display_name || 'Voto Memories'}</p>
                    <p className="text-sm text-voto-fg">{comment.body || 'Empty comment'}</p>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <p className="text-xs text-voto-muted">{new Date(comment.created_at).toLocaleString()}</p>
                      <div className="flex items-center gap-2">
                        <button
                          className="rounded-full px-2 py-1 text-xs font-medium text-voto-muted transition hover:bg-voto-raised hover:text-voto-fg disabled:opacity-60"
                          onClick={() => reportComment(comment.id)}
                          disabled={!!reportedComments[comment.id]}
                          type="button"
                        >
                          {reportedComments[comment.id] ? 'Reported' : 'Report'}
                        </button>
                        <button
                          aria-label={comment.liked_by_current_user ? 'Unlike comment' : 'Like comment'}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition ${
                            comment.liked_by_current_user ? 'bg-voto-coral/15 text-voto-coral' : 'text-voto-muted hover:bg-voto-raised hover:text-voto-fg'
                          }`}
                          onClick={() => handleCommentLike(comment)}
                          type="button"
                        >
                          <LikeIcon active={comment.liked_by_current_user} />
                          <span>{comment.likes_count || 0}</span>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mt-6">
            <h2 className="text-base font-semibold text-voto-fg">Tags</h2>
            {!poll.tags?.length && <p className="mt-3 rounded-xl bg-voto-surface p-4 text-voto-muted">No tags on this poll.</p>}
            {!!poll.tags?.length && (
              <div className="mt-3 flex flex-wrap gap-2">
                {poll.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/feed?tag=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-voto-surface px-3 py-1 text-xs font-medium text-voto-muted ring-1 ring-voto-line transition hover:text-voto-fg"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}
